import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Radio,
    RadioGroup,
    Select,
    Text,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import config from '../config';
import SelectedGameContext from '../context/SelectedGameContext';
import InventoryDataCaller from '../util/inventoryDataCaller';

// TODO: Add optionality
interface ItemDef {
    item_id: string;
    item_name: string;
    item_desc?: string;
    item_type: string;
    item_prop: Record<string, any>;
    item_owner: string;
    creator_license: string[];
    creator_user: string[];
    consumer_license: string[];
    consumer_user: string[];
    reader_license: string[];
    reader_user: string[];
    ttl: number;
}

enum AlterQuantityOperation {
    Add = 'add',
    Subtract = 'sub',
}

const isNotUndefinedOrEmpty = <T,>(s: T | undefined): s is T => !!s && s !== '';

interface EditItemProps {
    userId: string;
    itemId: string;
    isOpen: boolean;
    onClose: () => void;
}

export const EditItem: FC<EditItemProps> = ({ userId, itemId, isOpen, onClose }) => {
    const toast = useToast();
    const selectedGame = useContext(SelectedGameContext);
    const currLicense = selectedGame?.currLicense;
    const [itemQuantity, setItemQuantity] = useState<number>(0);
    const [itemDef, setItemDef] = useState<ItemDef | undefined>();
    const url = `${config.inventoryServiceURL}`;
    const { keycloak } = useKeycloak();
    const [username, setUsername] = useState<string | undefined>();
    const [actionValue, setActionValue] = useState<'alter' | 'set'>('alter');
    const [alterQuantity, setAlterQuantity] = useState<number>(0);
    const [setQuantity, setSetQuantity] = useState<number>(0);
    const [newQuantity, setNewQuantity] = useState<number>(0);
    const [addQuantityBool, setAddQuantityBool] = useState<boolean>(true);
    const [itemGameProp, setItemGameProp] = useState<Record<string, any>>({});
    const [itemTtl, setItemTtl] = useState<number>(0);
    const { isOpen: isAlertOpen, onClose: onAlertClose, onOpen: onAlertOpen } = useDisclosure();
    const cancelRef = useRef<HTMLButtonElement>(null);
    const alertFocusRef = useRef<HTMLButtonElement>(null); // The null is needed to appease the type system

    const getItemDef = async (itemId: string) => {
        const dc = new InventoryDataCaller(url, keycloak.token!);
        const itemResponse = await dc.getItem(itemId);
        if (itemResponse.status !== 200) {
            return toast({
                title: `Error`,
                description: `Error getting item definition`,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }

        const items: Array<ItemDef> = (await itemResponse.json()).data;
        if (!items || items.length !== 1) {
            return toast({
                title: `Error`,
                description: `Incorrect number of item definitions returned`,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }

        setItemDef(items[0]);
    };

    const getInventory = async (userId: string, itemDef: ItemDef) => {
        const dc = new InventoryDataCaller(url, keycloak.token!);
        const data = await dc.getInventory(userId, {
            itemId: itemDef.item_id,
            itemType: itemDef.item_type,
        });

        if (!data.ok) {
            return toast({
                title: `Error getting user's inventory`,
                description: `Error getting user's inventory`,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }

        const quantityData = await data.json();
        if (quantityData.data.length > 0) {
            const { quantity, game_prop, ttl } = quantityData.data[0];
            setItemQuantity(quantity);
            setItemGameProp(game_prop);
            setItemTtl(ttl);
        }
    };

    const getUsername = async () => {
        const resp = await fetch(`${config.socialServiceURL}/user/${userId}/profile/public`, {
            headers: {
                Authorization: `Bearer ${keycloak.token}`,
            },
        });
        if (resp.status !== 200) {
            return toast({
                title: 'Error getting username',
                description: `Unable to retrieve inventory owner's username: Error response ${resp.json()}`,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } else {
            const usernameData = await resp.json();
            setUsername(usernameData.data.username);
        }
    };

    const handleAlterQuantityChange = (_: string, valueAsNumber: number) => {
        if (valueAsNumber === valueAsNumber) {
            // is not Nan
            setAlterQuantity(valueAsNumber);
        }
    };

    const handleSetQuantityChange = (_: string, valueAsNumber: number) => {
        if (valueAsNumber === valueAsNumber) {
            // is not Nan
            setSetQuantity(valueAsNumber);
        }
    };

    const submit = async () => {
        onAlertClose();
        if (actionValue === 'set' && setQuantity === undefined) {
            return toast({
                title: 'No Quantity Set',
                description: 'You forgot to set a value for quantity, sir...',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }

        const quantityChange =
            actionValue === 'alter'
                ? { quantity_to_add: addQuantityBool ? alterQuantity : -alterQuantity }
                : { quantity_to_set: setQuantity! };

        const dc = new InventoryDataCaller(url, keycloak.token!);
        const response = await dc.updateInventory([
            {
                item_id: itemId,
                ...quantityChange,
                user_id: userId,
            },
        ]);

        if (response.status !== 200) {
            return toast({
                title: 'Unexpected Error',
                description: 'There was an error processing your request...soz lol',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } else {
            onClose();
            return toast({
                title: 'Huzzah!',
                description: 'Updated inventory item quantity successfully!',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleAlertOpen = () => {
        if (newQuantity >= 0) {
            onAlertOpen();
        } else {
            return toast({
                title: `Negative Quantity Error`,
                description: 'Cannot leave negative inventory item quantities, friend...',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleSubmit = () => {
        submit();
    };

    const handleModalClose = () => {
        onClose();
        setAlterQuantity(0);
        setSetQuantity(0);
    };

    useEffect(() => {
        alterQuantityCallback(itemQuantity, alterQuantity);
    }, [alterQuantity, addQuantityBool]);

    const alterQuantityCallback = (currQuantity: number, alterQuantity: number) => {
        if (addQuantityBool) {
            let finalQuantity = currQuantity + alterQuantity;
            setNewQuantity(finalQuantity);
        } else {
            let finalQuantity = currQuantity - alterQuantity;
            setNewQuantity(finalQuantity);
        }
    };

    useEffect(() => {
        if (userId !== '') {
            getUsername();
        }
    }, [userId]);

    useEffect(() => {
        if (itemId !== '') {
            getItemDef(itemId);
        }
    }, [currLicense, keycloak.token, url, userId, itemId]);

    useEffect(() => {
        if (isNotUndefinedOrEmpty(userId) && isNotUndefinedOrEmpty(itemDef)) {
            getInventory(userId, itemDef);
        }
    }, [currLicense, keycloak.token, url, userId, itemDef, itemId]);

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{itemDef?.item_name}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text>{username}'s inventory</Text>
                        <RadioGroup
                            onChange={(value) => {
                                setActionValue(value as 'alter' | 'set');
                            }}
                            value={actionValue}
                        >
                            <Flex w={'100%'} m={'1em 0'}>
                                <Radio value={'alter'} mr={'1em'}>
                                    Add/Subtract
                                </Radio>
                                <Radio value={'set'}>Set</Radio>
                            </Flex>
                        </RadioGroup>
                        {itemQuantity && <Text>Current Quantity: {itemQuantity}</Text>}
                        {actionValue === 'alter' && (
                            <>
                                <Flex direction={'row'} justify={'left'} gap={'20px'}>
                                    <Select
                                        maxWidth={'100px'}
                                        onChange={(e) =>
                                            setAddQuantityBool(
                                                e.target.value === AlterQuantityOperation.Add,
                                            )
                                        }
                                    >
                                        {Object.entries(AlterQuantityOperation).map(
                                            ([name, value]) => (
                                                <option key={value} value={value}>
                                                    {name}
                                                </option>
                                            ),
                                        )}
                                    </Select>
                                    <NumberInput
                                        width={'80%'}
                                        defaultValue={alterQuantity}
                                        value={alterQuantity ? alterQuantity : 0}
                                        min={0}
                                        onChange={handleAlterQuantityChange}
                                    >
                                        <NumberInputField />
                                        <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>
                                </Flex>
                                {newQuantity > 0 && <Text>New quantity: {newQuantity}</Text>}
                            </>
                        )}
                        {actionValue === 'set' && (
                            <NumberInput
                                defaultValue={itemQuantity} // Fix value
                                value={setQuantity}
                                min={0}
                                onChange={handleSetQuantityChange}
                            >
                                <NumberInputField />
                                <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                </NumberInputStepper>
                            </NumberInput>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={handleModalClose}>Cancel</Button>
                        <Button onClick={handleAlertOpen} ml={'1em'}>
                            Confirm
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <AlertDialog
                leastDestructiveRef={cancelRef}
                onClose={onAlertClose}
                isOpen={isAlertOpen}
                isCentered
                closeOnEsc
                closeOnOverlayClick
                motionPreset="slideInBottom"
            >
                <AlertDialogOverlay />
                <AlertDialogContent>
                    <AlertDialogHeader>
                        Confirm {username}'s {itemDef?.item_name} change
                    </AlertDialogHeader>
                    <AlertDialogBody>
                        Confirm that you would like to change {username}'s {itemDef?.item_name}{' '}
                        quantity to {actionValue === 'alter' ? newQuantity : setQuantity}, please
                        and thank!
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={alertFocusRef} onClick={onAlertClose}>
                            Cancel
                        </Button>
                        <Button type="submit" onClick={handleSubmit} ml={'1em'}>
                            Save
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default EditItem;
